// PersonnelDetailsPage.jsx
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Popconfirm, Row, Col, Dropdown, Space, Card, Avatar } from "antd";
import {
  EditFilled,
  LockFilled,
  UnlockFilled,
  DownOutlined,
  DeleteFilled,
  ReloadOutlined
} from "@ant-design/icons";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import Toast from "../../helpers/Toast";
import PageTitle from "../../components/utils/PageTitle";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import PersonnelEditForm from "../../components/personnel/PersonnelEditForm";
import { PersonnelService } from "../../services/PersonnelService";
import PersonnelDetails from "../../components/personnel/PersonnelDetails";
import { catchError } from "../../services/DaoService";
import PersonnelListByPersonnel from "../../components/personnel/PersonnelListByPersonnel";

const PersonnelDetailsPage = () => {
  const { uid } = useParams();
  const [personnel, setPersonnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const { check } = useUserContext();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    PersonnelService.getInstance()
      .find(uid)
      .then((response) => {
        setPersonnel(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [uid]);
  useEffect(() => {
    load();
  }, [load]);

  function openEditModal() {
    setEditModalVisible(true);
  }

  const handleEnable = () => {
    if (!personnel) {
      return;
    }
    PersonnelService.getInstance()
      .enable(uid)
      .then((response) => {
        Toast.success(response.message);
        setPersonnel(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleDisable = () => {
    if (!personnel) {
      return;
    }
    PersonnelService.getInstance()
      .disable(uid)
      .then((response) => {
        Toast.success(response.message);
        setPersonnel(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleDelete = () => {
    PersonnelService.getInstance()
      .remove(personnel.uid)
      .then(() => {
        navigate("/personnel");
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleUpdate = (updatedPersonnel) => {
    setPersonnel(updatedPersonnel);
    setEditModalVisible(false);
  };

  const menuItems = [
    {
      key: "1",
      label: (
        <>
          {personnel?.enabled ? (
            <Popconfirm
              title="Voulez-vous vraiment désactiver cet élément ?"
              onConfirm={handleDisable}
              okText="Désactiver" description="Vous ne pourrez plus utiliser cette donnée pour de futures enregistrements."
              cancelText="Annuler"
            >
              Désactiver
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Voulez-vous vraiment procéder ?"
              onConfirm={handleEnable}
              okText="Activer"
              cancelText="Annuler"
            >
              Activer
            </Popconfirm>
          )}
        </>
      ),
      danger: true,
      icon: personnel?.enabled ? (
        <LockFilled />
      ) : (
        <UnlockFilled title="Activer" />
      ),
      permission: personnel?.enabled
        ? "DISABLE-PERSONNEL"
        : "ENABLE-PERSONNEL",
    },
    {
      key: "2",
      label: (
        <Popconfirm
          title="Voulez-vous vraiment procéder à la suppression ?"
          onConfirm={handleDelete}
          okText="Oui"
          okButtonProps={{ danger: true }}
          cancelText="Non"
        >
          Supprimer
        </Popconfirm>
      ),
      icon: <DeleteFilled />,
      danger: true,
      permission: "DELETE-PERSONNEL",
    }
  ];

  const allowedMenuItems = menuItems.filter((menuItem) =>
    check(menuItem.permission)
  );

  return (
    <Main>
      <Breadcrumb
        parentLink="/personnel"
        canSee={check("VIEW-PERSONNELS")}
        parentLinkTitle="Liste des personnels"
        title={personnel?.name ?? "En chargement..."}
      />
      <PageTitle
        title={personnel ? personnel.name : "personnel"}
        subTitle="Détails du personnel"
      >
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Rafraîchir
        </Button>
        {check("EDIT-PERSONNEL") && (
          <Button
            type="default"
            icon={<EditFilled />}
            onClick={openEditModal}
            style={{ backgroundColor: ' #facc15' }}
          >
            Éditer
          </Button>
        )}
        {allowedMenuItems?.length > 0 && (
          <Dropdown
            menu={{ items: allowedMenuItems }}
          >
            <Button type="default" icon={<DownOutlined />}>
              Actions
            </Button>
          </Dropdown>
        )}
      </PageTitle>
      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <Row gutter={[5, 5]}>
          <Col span={24} lg={8} xl={6}>
            <Card style={{ textAlign: 'center' }}>
              <Avatar size={128} src={personnel?.avatar} />
            </Card>
          </Col>
          <Col span={24} lg={16} xl={18}>
            <Row gutter={[5, 5]}>
              <Col span={24}>
                <PersonnelDetails personnel={personnel} />
              </Col>
              {personnel && <Col span={24}>
                <PersonnelListByPersonnel personnel={personnel} />
              </Col>}
            </Row>
          </Col>
        </Row>
      )}
      {personnel ? <PersonnelEditForm
        visible={editModalVisible}
        uid={personnel.uid}
        onUpdate={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
      /> : null}
    </Main>
  );
};

export default PersonnelDetailsPage;